import { useState } from 'react'
import { Button, MenuItem, TextField, Typography } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import PropTypes from 'prop-types'
import { apiPost } from '../../generic/Api_Functions'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { buildClusterSendAlert } from './clusterSendResultAlert'

/**
 * The population pagination of the fabrication order table: page 0, size -1, no selection. It is
 * the very same shape the multiple delete of this window builds (FabricationOrder.jsx:812-828) to
 * turn a selection into rows. Built ON TOP of the received `pageable` so the active filters and
 * generic search of the window are preserved: the population is the FILTERED one (DG-11).
 *
 * @param {Object} pageable Selection payload of the table (carries the filters)
 * @returns {Object} Pagination that asks the server for the whole filtered population
 */
const populationPagination = (pageable) => ({
  columns: [],
  genericSearch: '',
  sortKey: [],
  sortMode: [],
  ...(pageable || {}),
  page: 0,
  size: -1,
  rows: [],
  allSelected: false,
  primaryKeys: ['id']
})

/**
 * The rows of a table answer, which is either a page or a plain array.
 *
 * @param {Object|Object[]} data Answer of the table data fetch
 * @returns {Object[]} The rows it carries
 */
const rowsOf = (data) => Array.isArray(data) ? data : (data?.content || [])

/**
 * The id of a selected row, which the table publishes either as an object or as a bare key.
 *
 * @param {Object|string|number} row Selected row
 * @returns {string} Its id as a string
 */
const idOf = (row) => String(row?.id !== undefined ? row.id : row)

/**
 * S34 (historia 107, PARTE 1): bulk "send to cluster node" action of the fabrication order window.
 * It is a second entry of the window's `multipleSelectionActions`, never part of the status
 * ActionSelector, so the other bulk actions stay exactly as they are (S38).
 *
 * Contract with Sun (`FabricationOrderClusterSendRest`): the send names the orders by CODE, not by
 * id (RN5), and the table selection only carries ids (`primaryKeys: ['id']`). The codes are
 * therefore resolved against the table itself before the dialog opens -- a read, never a send, so
 * nothing has been requested while the user is still confirming (S35).
 *
 * @param {Object} props Props object
 * @param {string[]} props.nodes Destination nodes, current node already removed (DG-18)
 * @param {Function} props.dataFetch Table data fetch, used to resolve the selected codes (S35/DG-11)
 * @param {Object} props.pageable Selection payload of the table (carries the active filters)
 * @param {Object} props.multipleSelectionValue Current table selection
 * @param {Function} props.showAlert Alert callback of the fabrication order window
 * @param {Function} props.onSent Called after a send, to reset the selection
 * @returns {JSX.Element} The action button plus its confirmation dialog
 */
const FabricationOrderClusterSendAction = ({ nodes, dataFetch, pageable, multipleSelectionValue, showAlert, onSent }) => {
  const { t_ } = useSmartTranslation()
  const [open, setOpen] = useState(false)
  const [targetNode, setTargetNode] = useState('')
  const [orderCodes, setOrderCodes] = useState([])
  const [selectedCount, setSelectedCount] = useState(0)

  const selectedRows = multipleSelectionValue?.rows || []
  const allSelected = multipleSelectionValue?.allSelected || false

  /**
   * The codes of the fabrication orders the user is about to send.
   *
   * S35/DG-11: with "select all" the table empties `rows`, so neither the codes nor the count can
   * come from the visible rows -- both come from the filtered population the server reports. The
   * resolved list is what the dialog counts, so the number the user confirms is exactly the number
   * of orders that will travel.
   *
   * @returns {Promise<string[]>} Codes of the selected fabrication orders
   */
  const resolveOrderCodes = async () => {
    if (typeof dataFetch !== 'function' || (!allSelected && selectedRows.length === 0)) {
      return []
    }
    const population = rowsOf(await dataFetch(populationPagination(pageable)))
    const selectedIds = new Set(selectedRows.map(idOf))
    const selected = allSelected ? population : population.filter((row) => selectedIds.has(idOf(row)))
    return selected.map((row) => row?.fabricationOrder || '').filter((code) => code !== '')
  }

  const openDialog = async () => {
    setTargetNode('')
    setOrderCodes([])
    setSelectedCount(allSelected ? 0 : selectedRows.length)
    setOpen(true)
    const codes = await resolveOrderCodes()
    setOrderCodes(codes)
    setSelectedCount(codes.length)
  }

  const closeDialog = () => {
    setOpen(false)
  }

  // S37/DG-5/DG-7: the server answers one outcome per fabrication order, so the alert reflects what
  // each one came to. An HTTP failure is a global failure (400 for what invalidates the whole
  // request, 403 for the missing permission), handled exactly as the bulk status change of this
  // same window does.
  const send = async () => {
    setOpen(false)
    // S41/V1/E1: never request a send for an empty selection. The bulk toolbar is not shown without
    // a selection, so this is the defensive half of the validation.
    if (orderCodes.length === 0) {
      showAlert({ severity: 'warning', title: t_('No fabrication orders selected'), message: '' })
      return
    }
    try {
      const outcomes = await apiPost('fabricationOrder/clusterSend', {
        fabricationOrderCodes: orderCodes,
        targetNodeName: targetNode
      })
      showAlert(buildClusterSendAlert(outcomes, t_))
      onSent()
    } catch (error) {
      // The body of a 400 is `{ code, message }`: `code` is a stable i18n key, so it is what the
      // user gets when the failure carries no readable message of its own.
      const reason = error?.message || error?.data?.code || ''
      showAlert({
        title: t_('Error sending fabrication orders:'),
        message: reason === '' ? '' : t_(reason),
        severity: error?.name || 'error'
      })
    }
  }

  return (
    <>
      <Button color='primary' variant='outlined' startIcon={<SendIcon />} onClick={openDialog}>
        {t_('Send to node')}
      </Button>
      <SmartDialog
        setOpen={open}
        title='default'
        autoClose={false}
        accept={false}
        cancel
        cancelCallback={closeDialog}
        renderComponent={
          <TextField
            select
            fullWidth
            value={targetNode}
            label={t_('Select destination node')}
            helperText={targetNode === '' ? t_('Destination node is required') : ''}
            onChange={(event) => setTargetNode(event.target.value)}
          >
            {nodes.map((node) => (
              <MenuItem key={node} value={node}>{node}</MenuItem>
            ))}
          </TextField>
        }
        message={t_('You are going to send {{count}} fabrication orders', { count: selectedCount })}
        renderCustomButtons={
          // S41/V2/E2: the send cannot be confirmed until a destination node is chosen. Same
          // pattern as ForcedReturnDialog: only the confirmation is disabled, so cancelling is
          // always available (SmartDialog's `disableButtons` would disable cancel too).
          <Button color='primary' variant='outlined' disabled={targetNode === ''} onClick={send}>
            <Typography>{t_('ACCEPT')}</Typography>
          </Button>
        }
      />
    </>
  )
}

FabricationOrderClusterSendAction.propTypes = {
  nodes: PropTypes.array.isRequired,
  dataFetch: PropTypes.func,
  pageable: PropTypes.object,
  multipleSelectionValue: PropTypes.object,
  showAlert: PropTypes.func.isRequired,
  onSent: PropTypes.func.isRequired
}

export default FabricationOrderClusterSendAction
